import React, { Component } from 'react';
import { BlazeTableOptions } from 'components/blazeTable/BlazeTable.types';
import BlazeTable from 'components/blazeTable/BlazeTable';
import { ApplicationState } from 'store/rootReducer';
import { connect } from 'react-redux';
import i18n from 'i18n';
import { WithTranslation, withTranslation } from 'react-i18next';
import _ from 'lodash';
import { HttpRequest } from 'types/rest/analyticsTypes';
import { getTableOptions } from 'views/Analytics/AnalyticsMockService/Inspection/InspectionViewMar';
import { HttpDataState } from 'store/analytics/data/analyticsDataTypes';
import InspectionHeader from 'views/Analytics/AnalyticsMockService/Inspection/Header/InspectionHeader';
import { MockServiceAnalytics } from 'types/rest/mockServiceTypes';
import {
  fetchWebhooksListThunk,
  fetchWebhookViewRequestDetailThunk,
} from 'store/analytics/data/analyticsWebhookActions';
import {
  changeWebhookViewActivePage,
  changeWebhookViewPageSize,
  selectWebhookDetailTab,
  setWebhookSearchKeyword,
} from 'store/analytics/ui/subViews/webhookView/webhookViewActions';
import { REFRESH_WEBHOOK_VIEW, SEARCH_FILTER_RESULTS } from 'utils/mixpanelEvents';
import MixpanelService from 'utils/mixpanelService';
import {
  getCommonDataFromDataContext,
  getCommonDataFromUiContext,
  getFilteredHttpRequests,
  getHttpCallDetail,
  getHttpViewTableOptions,
} from '../Common/HttpViewUtils';
import { DetailTabType } from '../../../../store/analytics/ui/analyticsUiTypes';

interface WebHookListStateProps {
  isSaveTxnModalShown: boolean;
  /** Request to be saved in the modal */
  requestToBeSaved: HttpRequest | null;
}

interface FilteredResults {
  requests: any[];
  tableOptions: BlazeTableOptions;
  sliceData: any;
}

interface WebhookViewProps {
  mockService: MockServiceAnalytics;
}

/**
 * Request List.
 */
export class WebhookView extends Component<AllProps, WebHookListStateProps> {
  tableRef: any = React.createRef<BlazeTable>();

  state = {
    isSaveTxnModalShown: false,
    requestToBeSaved: null,
  };

  componentDidMount(): void {
    this.getWebhookDataForFirstTime();
  }

  componentDidUpdate() {
    this.getWebhookDataForFirstTime();
  }

  /**
   * Loads data on mount event / tab change when they are not loaded yet.
   */
  getWebhookDataForFirstTime = () => {
    const contextsAreReady = !_.isNil(this.props.activeDataContext) && !_.isNil(this.props.activeUiContext);
    if (!contextsAreReady) {
      return;
    }
    const dataState: HttpDataState = this.props.activeDataContext.webhook;
    if (
      this.props.activeUiContext!.activeTab === 'webhook' &&
      dataState.isLoadingData === false &&
      dataState.isDetailRequested === false &&
      dataState.lastRefreshTs === null
    ) {
      this.props.refreshWebHooks(this.props.activeTab);
    }
  };

  callRefresh = () => {
    MixpanelService.track(REFRESH_WEBHOOK_VIEW(this.props.activeTab));
    this.props.refreshWebHooks(this.props.activeTab);
  };

  handleFilterChange = async (value: string) => {
    await this.props.setWebhookSearchKeyword(value);
    this.sendMixPanelEvent();
  };

  sendMixPanelEvent = _.debounce(() => {
    MixpanelService.track(SEARCH_FILTER_RESULTS());
  }, 600);

  /**
   * Gets list of request from active data context in redux.
   */
  getWebhooksFromDataContext = () => {
    if (_.isNil(this.props.activeDataContext)) {
      return [];
    }
    const txiDataState: HttpDataState = this.props.activeDataContext.webhook;
    return (txiDataState.httpRequests as HttpRequest[]) || [];
  };

  callUpdate = (tabName: DetailTabType) => {
    this.props.selectWebhookDetailTab(tabName);
  };

  /**
   * Builds detail of the webhook.
   * */
  getWebhookDetail = () =>
    getHttpCallDetail(
      this.callUpdate,
      this.props.activeUiContext,
      'webhook',
      this.props.activeDataContext,
      this.props.refreshWebHooks,
    );

  render() {
    // UI state
    const { activePage, pageSize, selectedHttpReq, searchKeyword } = getCommonDataFromUiContext(
      this.props.activeUiContext,
      'webhookView',
    );

    // Data state
    const { isRequestListLoading, lastRefresh, tabId } = getCommonDataFromDataContext(
      this.props.activeDataContext,
      'webhook',
    );

    const webhooks = this.getWebhooksFromDataContext();

    const tableOptions: BlazeTableOptions = getHttpViewTableOptions(
      reqId => this.props.fetchWebhookViewRequestDetailThunk(reqId, tabId),
      selectedHttpReq,
      {
        pageSize: pageSize,
        total: webhooks.length,
        activePage: activePage,
      },
      'webhook',
    );

    const filteredItems = getFilteredHttpRequests(searchKeyword, this.getWebhooksFromDataContext());
    const filterResult: FilteredResults = getTableOptions(filteredItems, searchKeyword, tableOptions, pageSize);

    return (
      <>
        <InspectionHeader
          refreshHandler={this.callRefresh}
          requests={webhooks}
          onSearchKeywordChanged={this.handleFilterChange}
          lastRefresh={lastRefresh}
          searchKeyword={searchKeyword}
          title={i18n.t('analytics:webhook.title')}
          msType={this.props.mockService.type}
        ></InspectionHeader>
        <BlazeTable
          isLoading={isRequestListLoading}
          options={filterResult.tableOptions}
          onPageSizeChange={this.props.changeWebhookViewPageSize}
          onPageChange={this.props.changeWebhookViewActivePage}
          data={filterResult.sliceData}
          onRef={(ref: any) => (this.tableRef = ref)}
          showPageSizeDropdown={true}
        />

        {/* Detail for selected webhook, show it only if there is detail selected */}
        {!_.isNil(selectedHttpReq) && !_.isNil(lastRefresh) && this.getWebhookDetail()}
      </>
    );
  }
}

const mapStateToProps = ({ analytics }: ApplicationState) => {
  return {
    activeTab: analytics.ui.activeTab,
    activeUiContext: analytics.ui.activeUiContext,
    activeDataContext: analytics.data.activeDataContext,
  };
};

const mapDispatchToProps = {
  changeWebhookViewActivePage,
  changeWebhookViewPageSize,
  fetchWebhookViewRequestDetailThunk,
  setWebhookSearchKeyword,
  refreshWebHooks: fetchWebhooksListThunk,
  selectWebhookDetailTab,
};

type MapStateToProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;
type AllProps = MapStateToProps & WithTranslation & DispatchProps & WebhookViewProps;

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(WebhookView));
